import { z } from 'zod';
import { AvailableLanguagesEnum } from './available-languages.enum';
import { PrintType } from './model.model';

const entityIdSchema = z.number().int().min(1);
const decimalStringSchema = z.string().regex(/^\d+(?:\.\d+)?$/);
const printingQueryEntityIdSchema = z.union([entityIdSchema, z.string().regex(/^\d+$/).transform(Number).pipe(entityIdSchema)]);
const printingQueryDateSchema = z
  .union([z.string(), z.date()])
  .pipe(z.coerce.date())
  .transform(date => date.toISOString());

export const printingQuerySchema = z
  .object({
    ids: z.array(printingQueryEntityIdSchema).optional(),
    modelIds: z.array(printingQueryEntityIdSchema).optional(),
    userIds: z.array(printingQueryEntityIdSchema).optional(),
    adminIds: z.array(printingQueryEntityIdSchema).optional(),
    startDate: printingQueryDateSchema.optional(),
    endDate: printingQueryDateSchema.optional(),
    offset: z.number().int().min(0).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    exclude: z.array(printingQueryEntityIdSchema).optional(),
  })
  .strict();

export const printingAdminSchema = z
  .object({
    id: entityIdSchema,
    email: z.string(),
    fullName: z.string(),
    phone: z.string(),
    address: z.string(),
    country: z.string(),
    allowedPPF: decimalStringSchema,
    allowedWindows: decimalStringSchema,
    language: z.enum(AvailableLanguagesEnum),
    isActive: z.boolean(),
  })
  .strict();

export const printingUserSchema = z
  .object({
    id: entityIdSchema,
    email: z.string(),
    fullName: z.string(),
    phone: z.string(),
    address: z.string(),
    country: z.string(),
    allowedPPF: decimalStringSchema,
    allowedWindows: decimalStringSchema,
    language: z.enum(AvailableLanguagesEnum),
    isActive: z.boolean(),
    admin: printingAdminSchema,
  })
  .strict();

export const printingPieceSchema = z
  .object({
    label: z.string(),
    color: z.string(),
    class: z.string().optional(),
    x: z.number(),
    y: z.number(),
    filepath: z.string(),
    width: decimalStringSchema,
    height: decimalStringSchema,
  })
  .strict();

export const printingModelSchema = z
  .object({
    id: entityIdSchema,
    name: z.string(),
    description: z.string().nullable().optional(),
    picture: z.string(),
    serieId: entityIdSchema,
    serie: z
      .object({
        id: entityIdSchema,
        name: z.string(),
        brandId: entityIdSchema,
        brand: z
          .object({
            id: entityIdSchema,
            name: z.string(),
            logo: z.string(),
          })
          .strict(),
      })
      .strict(),
  })
  .strict();

export const printingPrintableDataSchema = z
  .object({
    id: entityIdSchema,
    modelId: entityIdSchema,
    preview: z.string(),
    pieces: z.array(printingPieceSchema),
    type: z.enum(PrintType),
    bbox: z
      .object({
        x: z.number(),
        y: z.number(),
        width: decimalStringSchema,
        height: decimalStringSchema,
      })
      .strict(),
    model: printingModelSchema,
  })
  .strict();

export const plotterConfigSchema = z
  .object({
    unit: z.number(),
    switchAxis: z.boolean(),
    mirror: z.boolean(),
  })
  .strict();

export const printingStatusSchema = z.enum(['SUCCESS', 'FAIL']);
export const printingFailureSchema = z
  .object({
    message: z.string(),
    tracelog: z.string().optional(),
  })
  .strict();

/** Public projection returned by GET /api/printings/list. */
export const printingSchema = z
  .object({
    id: entityIdSchema,
    userId: entityIdSchema.nullable(),
    user: printingUserSchema.nullable(),
    printableData: printingPrintableDataSchema,
    printedPieces: z.array(z.number().int().min(0)),
    plotterConfig: plotterConfigSchema,
    printedArea: decimalStringSchema,
    status: printingStatusSchema,
    failError: printingFailureSchema.nullable(),
    plotter: z
      .object({
        id: entityIdSchema,
        name: z.string(),
      })
      .strict(),
    daemonId: z.string(),
    deviceId: z.string(),
    createdAt: z.string(),
  })
  .strict();

export const printingListResponseSchema = z
  .object({
    statusCode: z.number().int(),
    data: z
      .object({
        listing: z.array(printingSchema),
        total: z.number().int().min(0),
      })
      .strict(),
  })
  .strict();

export type PrintingAdmin = z.infer<typeof printingAdminSchema>;
export type PrintingUser = z.infer<typeof printingUserSchema>;
export type PrintingPiece = z.infer<typeof printingPieceSchema>;
export type PrintingModel = z.infer<typeof printingModelSchema>;
export type PrintingPrintableData = z.infer<typeof printingPrintableDataSchema>;
export type PlotterConfig = z.infer<typeof plotterConfigSchema>;
export type PrintingStatus = z.infer<typeof printingStatusSchema>;
export type PrintingFailure = z.infer<typeof printingFailureSchema>;
export type Printing = z.infer<typeof printingSchema>;
export type PrintingListResponse = z.infer<typeof printingListResponseSchema>;
export type PrintingList = PrintingListResponse['data'];
export type PrintingQuery = z.input<typeof printingQuerySchema>;

export type ExtendedPrinting = Printing;

export type FilterQuery = {
  startDate?: string | null;
  endDate?: string | null;
  modelIds?: string | null;
  adminIds?: string | null;
  userIds?: string | null;
};
